import React, { useCallback, useState, useEffect } from 'react';
import {
    DmsTextField,
    DmsSelect,
    useGet,
    intl,
    fetchData,
    HTTP_STATUS
} from 'dmsbasereact';
import { Box, Typography, Grid } from '@mui/material';
import { SettingsViewEdit } from '../../components/SettingsLayouts';

/**
 * LogConfigurationViewContent - View mode for log configuration settings
 * Log level types are fetched from backend API to avoid hardcoding enum values
 */
const LogConfigurationViewContent = ({ data, logLevelTypes }) => {
    // Build labels map from backend data
    const logLevelLabels = {};
    (logLevelTypes || []).forEach(type => {
        logLevelLabels[type.value] = type.label;
    });

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                        {intl.get('LogLevel').d('Log Level')}
                    </Typography>
                    <Typography variant="body1">
                        {logLevelLabels[data.min_log_level] || data.min_log_level}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                        {intl.get('HourThreshold').d('Hour threshold')}
                    </Typography>
                    <Typography variant="body1">
                        {data.hour_threshold ?? 24}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                        {intl.get('RequestWaitTime').d('Request wait time')}
                    </Typography>
                    <Typography variant="body1">
                        {data.request_wait_time ?? 60}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                        {intl.get('SizeOfLogList').d('Size of log list')}
                    </Typography>
                    <Typography variant="body1">
                        {data.size_of_log_list ?? 100}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

/**
 * LogConfigurationEditContent - Edit mode for log configuration settings
 * Log level types are fetched from backend API to avoid hardcoding enum values
 */
const LogConfigurationEditContent = ({ formData, onFieldChange, logLevelTypes }) => {
    // Convert backend types to select options
    const logLevelOptions = (logLevelTypes || []).map(type => ({
        value: type.value,
        label: type.label
    }));

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DmsSelect
                        name="min_log_level"
                        label={intl.get('LogLevel').d('Log Level')}
                        fullWidth
                        value={formData.min_log_level}
                        onChange={(value) => onFieldChange('min_log_level', value)}
                        options={logLevelOptions}
                        helperText={intl.get('LogLevelHelp').d('Minimum level of logs to record')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DmsTextField
                        name="hour_threshold"
                        label={intl.get('HourThreshold').d('Hour threshold')}
                        type="number"
                        fullWidth
                        value={formData.hour_threshold ?? 24}
                        onChange={(e) => onFieldChange('hour_threshold', e.target.value)}
                        helperText={intl.get('HourThresholdHelp').d('Hour threshold for log processing')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DmsTextField
                        name="request_wait_time"
                        label={intl.get('RequestWaitTime').d('Request wait time')}
                        type="number"
                        fullWidth
                        value={formData.request_wait_time ?? 60}
                        onChange={(e) => onFieldChange('request_wait_time', e.target.value)}
                        helperText={intl.get('RequestWaitTimeHelp').d('Wait time for requests in seconds')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DmsTextField
                        name="size_of_log_list"
                        label={intl.get('SizeOfLogList').d('Size of log list')}
                        type="number"
                        fullWidth
                        value={formData.size_of_log_list ?? 100}
                        onChange={(e) => onFieldChange('size_of_log_list', e.target.value)}
                        helperText={intl.get('SizeOfLogListHelp').d('Number of logs to display per page')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

/**
 * LogsSettings - Log configuration settings using view/edit mode pattern.
 * Fetches log level types from backend to avoid hardcoding enum values.
 */
export const LogsSettings = () => {
    const { data, isLoading, refetch } = useGet('/log_settings', null, true);
    const [logLevelTypes, setLogLevelTypes] = useState([]);

    // Fetch log level types from backend on mount
    useEffect(() => {
        const loadLogLevelTypes = async () => {
            try {
                const response = await fetchData('/log_settings/types', 'GET', true);
                if (response?.data?.types) {
                    setLogLevelTypes(response.data.types);
                }
            } catch (error) {
                console.error('Failed to load log level types:', error);
            }
        };
        loadLogLevelTypes();
    }, []);

    const prepareFormData = useCallback((settingsData) => {
        return {
            min_log_level: settingsData.min_log_level,
            hour_threshold: settingsData.hour_threshold ?? 24,
            request_wait_time: settingsData.request_wait_time ?? 60,
            size_of_log_list: settingsData.size_of_log_list ?? 100,
        };
    }, []);

    const buildPayload = useCallback((formData, originalData) => {
        return {
            min_log_level: parseInt(formData.min_log_level, 10),
            hour_threshold: parseInt(formData.hour_threshold, 10),
            request_wait_time: parseInt(formData.request_wait_time, 10),
            size_of_log_list: parseInt(formData.size_of_log_list, 10),
            // Include required fields from original data (not editable in UI)
            limit_to_keep_for_time: originalData?.limit_to_keep_for_time ?? 0,
            limit_to_keep_for_quantity: originalData?.limit_to_keep_for_quantity ?? -1,
            status_for_time: originalData?.status_for_time ?? false,
            status_for_quantity: originalData?.status_for_quantity ?? false,
        };
    }, []);

    const handleSave = useCallback(async (payload) => {
        try {
            const response = await fetchData('/log_settings', 'PUT', true, payload, false, true);

            if (response.status === HTTP_STATUS.OK_200) {
                // Return the updated data
                return response.data?.data || response.data;
            }

            return null;
        } catch (error) {
            console.error('Error saving log settings:', error);
            return null;
        }
    }, []);

    const settingsData = data?.data || data;

    return (
        <Box>
            <SettingsViewEdit
                data={settingsData}
                loading={isLoading}
                title={intl.get('LogConfiguration').d('Log Configuration')}
                description={intl.get('LogConfigurationDescription').d(
                    'Configure logging behavior and performance settings.'
                )}
                viewContent={LogConfigurationViewContent}
                editContent={LogConfigurationEditContent}
                prepareFormData={prepareFormData}
                buildPayload={buildPayload}
                onSave={handleSave}
                onDataUpdated={refetch}
                confirmTitle={intl.get('ConfirmSaveLogSettings').d('Confirm Save Log Settings')}
                confirmMessage={intl.get('ConfirmSaveLogSettingsMessage').d(
                    'Are you sure you want to save these log retention settings?'
                )}
                viewContentProps={{ logLevelTypes }}
                editContentProps={{ logLevelTypes }}
            />
        </Box>
    );
};
